/**
 * API Client for GRC Central Admin
 */

const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_BASE_URL || 'https://grc-central-admin.port-technology-account.workers.dev')
  : ''

export interface Tenant {
  id: string
  slug: string
  company_name: string
  contact_email: string
  status: 'provisioning' | 'active' | 'suspended' | 'deleted'
  tier: 'free' | 'pro' | 'enterprise'
  subdomain: string
  worker_name: string
  db_name: string
  r2_bucket: string
  kv_namespace: string
  current_version: string
  created_at: number
  updated_at?: number
}

export interface TenantUsage {
  id: string
  tenant_id: string
  date: string
  api_requests: number
  storage_bytes: number
  users_count: number
  controls_count: number
  evidence_count: number
}

export interface SystemStats {
  total_tenants: number
  active_tenants: number
  total_users: number
  total_storage_gb: number
  api_requests_today: number
  monthly_revenue: number
}

class AdminApiClient {
  private token: string | null = null

  constructor() {
    this.token = localStorage.getItem('admin_token')
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('admin_token', token)
    } else {
      localStorage.removeItem('admin_token')
    }
  }

  getToken(): string | null {
    return this.token
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {}

    const token = this.token || localStorage.getItem('admin_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    if (options.headers) {
      Object.assign(headers, options.headers)
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed')
    }

    return data
  }

  // Health check
  async getHealth() {
    return this.request<{
      status: string
      service: string
      environment: string
      timestamp: number
    }>('/health')
  }

  // Auth endpoints
  async requestCode(email: string) {
    return this.request<{
      success: boolean
      message: string
      session_token: string
    }>('/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async verifyCode(email: string, code: string, session_token: string) {
    return this.request<{
      success: boolean
      message: string
      token: string
      user: {
        id: string
        username: string
        email: string
        role: string
      }
    }>('/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code, session_token }),
    })
  }

  async resendCode(email: string, session_token: string) {
    return this.request<{
      success: boolean
      message: string
    }>('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ email, session_token }),
    })
  }

  async getMe() {
    return this.request<{
      success: boolean
      user: {
        id: string
        username: string
        email: string
        role: string
      }
    }>('/auth/me')
  }

  async validateMagicLink(token: string) {
    return this.request<{
      success: boolean
      message: string
      email?: string
    }>(`/auth/validate-magic-link/${token}`)
  }

  async verifyMagicLink(token: string) {
    return this.request<{
      success: boolean
      message: string
      token?: string
      user?: {
        id: string
        username: string
        email: string
        role: string
      }
    }>(`/auth/magic-link/${token}`)
  }

  async logout() {
    return this.request<{
      success: boolean
      message: string
    }>('/auth/logout', {
      method: 'POST',
    })
  }

  // Tenant endpoints
  async getTenants() {
    return this.request<{
      success: boolean
      tenants: Tenant[]
      count: number
    }>('/api/tenants')
  }

  async getTenant(id: string) {
    return this.request<{
      success: boolean
      tenant: Tenant
      usage: TenantUsage[]
    }>(`/api/tenants/${id}`)
  }

  async createTenant(data: {
    slug: string
    company_name: string
    contact_email: string
    tier?: 'free' | 'pro' | 'enterprise'
  }) {
    return this.request<{
      success: boolean
      tenant: Tenant
    }>('/api/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateTenant(id: string, data: Partial<{
    company_name: string
    contact_email: string
    status: 'active' | 'suspended'
    tier: 'free' | 'pro' | 'enterprise'
  }>) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteTenant(id: string) {
    return this.request<{
      success: boolean
      message: string
    }>(`/api/tenants/${id}`, {
      method: 'DELETE',
    })
  }

  // Stats endpoint
  async getSystemStats() {
    return this.request<{
      success: boolean
      stats: SystemStats
    }>('/api/stats')
  }

  // Framework templates
  async getFrameworkTemplates() {
    return this.request<{
      success: boolean
      frameworks: Array<{
        id: string
        name: string
        display_name: string
        description: string
        version: string
        controls_count: number
      }>
    }>('/api/frameworks')
  }

  // Migration endpoints
  async getMigrations() {
    return this.request<{
      success: boolean
      migrations: Array<{
        version: string
        name: string
        description: string | null
        is_destructive: boolean
        checksum: string
        created_at: number
      }>
      count: number
    }>('/api/migrations')
  }

  async getMigrationStatus() {
    return this.request<{
      success: boolean
      summary: {
        total_tenants: number
        current: number
        behind: number
        unknown: number
        latest_version: string | null
      }
      tenants: Array<{
        tenant_id: string
        slug: string
        company_name: string
        tenant_status: string
        db_name: string
        current_version: string
        migration_status: string
        is_current: boolean
        last_migration_at: number | null
      }>
      available_migrations: Array<{
        version: string
        name: string
      }>
    }>('/api/migrations/status')
  }

  async getMigrationRuns(limit = 20, offset = 0) {
    return this.request<{
      success: boolean
      runs: Array<{
        id: string
        migration_version: string
        migration_name: string
        triggered_by: string
        trigger_type: string
        started_at: number
        completed_at: number
        status: string
        tenants_total: number
        tenants_success: number
        tenants_failed: number
        tenants_skipped: number
        dry_run: boolean
        github_run_url: string | null
      }>
      pagination: {
        total: number
        limit: number
        offset: number
      }
    }>(`/api/migrations/runs?limit=${limit}&offset=${offset}`)
  }

  async getMigrationRun(id: string) {
    return this.request<{
      success: boolean
      run: {
        id: string
        migration_version: string
        migration_name: string
        triggered_by: string
        trigger_type: string
        started_at: number
        completed_at: number
        status: string
        tenants_total: number
        tenants_success: number
        tenants_failed: number
        tenants_skipped: number
        dry_run: boolean
        github_run_url: string | null
      }
      tenant_details: Array<{
        id: string
        tenant_id: string
        slug: string
        company_name: string
        status: string
        execution_time_ms: number | null
        error_message: string | null
      }>
    }>(`/api/migrations/runs/${id}`)
  }
}

export const adminApi = new AdminApiClient()
